import { memo, useMemo } from "react";

// material-ui
import { useTheme } from "@mui/material/styles";
import { Box, Drawer, Stack, useMediaQuery } from "@mui/material";

// third-party
import PerfectScrollbar from "react-perfect-scrollbar";

// project imports
import MenuCard from "./MenuCard";
import MenuList from "../MenuList";
import LogoSection from "../LogoSection";
import MiniDrawerStyled from "./MiniDrawerStyled";
import Chip from "@stories/ui-component/extended/Chip";

import { drawerWidth } from "constant";

// ==============================|| SIDEBAR DRAWER ||============================== //
interface SidebarProps {
  isDrawOpen: boolean;
  selectedItem: string[];
  setIsDrawOpen: (open: boolean) => void;
  setSelectedItem: (item: string[]) => void;
}

const Sidebar = ({
  isDrawOpen,
  selectedItem,
  setIsDrawOpen,
  setSelectedItem,
}: SidebarProps) => {
  const theme = useTheme();
  const matchUpMd = useMediaQuery(theme.breakpoints.up("md"));
  const matchDownMd = useMediaQuery(theme.breakpoints.down("md"));
  const logo = useMemo(
    () => (
      <Box sx={{ display: "flex", p: 2 }}>
        <LogoSection />
      </Box>
    ),
    []
  );

  const drawerContent = (
    <>
      <MenuList
        isDrawOpen={isDrawOpen}
        setIsDrawOpen={setIsDrawOpen}
        selectedItem={selectedItem}
        setSelectedItem={setSelectedItem}
      />
      {isDrawOpen && (
        <Stack direction="row" justifyContent="center" sx={{ mb: 2 }}>
          <Chip
            label={process.env.REACT_APP_VERSION}
            disabled
            chipcolor="secondary"
            size="small"
            sx={{ cursor: "pointer" }}
          />
        </Stack>
      )}
    </>
  );

  const drawerSX = {
    paddingLeft: isDrawOpen ? "16px" : 0,
    paddingRight: isDrawOpen ? "16px" : 0,
    marginTop: isDrawOpen ? 0 : "20px",
  };

  const drawer = matchDownMd ? (
    <Box sx={drawerSX}>{drawerContent}</Box>
  ) : (
    <PerfectScrollbar
      component="div"
      style={{
        height: !matchUpMd ? "calc(100vh - 56px)" : "calc(100vh - 88px)",
        ...drawerSX,
      }}
    >
      {drawerContent}
    </PerfectScrollbar>
  );

  return (
    <Box
      component="nav"
      sx={{ flexShrink: { md: 0 }, width: matchUpMd ? drawerWidth : "auto" }}
      aria-label="mailbox folders"
    >
      {matchDownMd || isDrawOpen ? (
        <Drawer
          variant={matchUpMd ? "persistent" : "temporary"}
          anchor="left"
          open={isDrawOpen}
          onClose={() => setIsDrawOpen(!isDrawOpen)}
          sx={{
            "& .MuiDrawer-paper": {
              mt: matchDownMd ? 0 : 11,
              zIndex: 1099,
              width: drawerWidth,
              background: theme.palette.background.default,
              color: theme.palette.text.primary,
              borderRight: "none",
            },
          }}
          ModalProps={{ keepMounted: true }}
          color="inherit"
        >
          {matchDownMd && logo}
          {drawer}
        </Drawer>
      ) : (
        <MiniDrawerStyled variant="permanent" open={isDrawOpen}>
          {logo}
          {drawer}
        </MiniDrawerStyled>
      )}
    </Box>
  );
};

export default Sidebar;
